<template>
  <section>
    <!-- DRAG BLOCKS: -->
    <div class="blocks">
      <div v-for="(category, key, catIndex) in blocks" :key="catIndex"
        class="blocks__wrapper">
        <h4 class="blocks__header" :style="{'border-color': borderColors[catIndex] }">
          {{ key }}s
        </h4>
        <ul class="blocks__list">
          <draggable
            :list="category"
            :options="{ group: { name: 'blocks', pull: 'clone', put: false }, sort: false }"
            class="blocks__list">
            <li v-for="(block, blockIndex) in category" :key="blockIndex"
              :class="['blocks__item', block.block_type]"
              :data-block-type="block.block_type"
              @click="showInfo = !showInfo"
              @mouseleave="showInfo = false">
              <div class="blocks__button text-uppercase">
                {{ block.name }}
              </div>
            <!-- BLOCK INFO -->
            <div v-if="showInfo" class="blocks__info"
              :style="{'border-color': borderColors[catIndex] }">
              <ul>
                <li>
                  <img class="blocks__photo" :src="domain + block.photo" alt="">
                  <button
                    :class="['blocks__button', block.block_type]">
                    {{ block.name }}
                  </button>
                </li>
                <li><span>Molecular Weight:</span><span> {{block.mw}} </span></li>
                <li><span>Polarity:</span><span>
                  {{block.polarity ? 'polar' : 'non-polar'}} </span></li>
                <li><span>Solubility:</span><span>
                  {{block.solubility ? 'soluble' : 'in-soluble'}} </span></li>
                <li><span>H-Bond Donors:</span><span> {{block.h_bond_donors}} </span></li>
              <li><span>H-Bond Acceptors:</span><span> {{block.h_bond_acceptors}} </span></li>
              </ul>
            </div>
            </li>
          </draggable>
        </ul>
      </div>
    </div>
    <!-- HEADING -->
    <!-- <slot></slot> -->
    <!-- DROP BOARD -->
    <div class="board" id="pdf-game-board">
      <div class="board__row" v-for="(row, indexRow) in boardDimensions.height" :key="indexRow">
          <div class="board__item"
            v-if="wells.length"
            v-for="(column, indexCol) in boardDimensions.width"
            :key="indexCol">
            <span class="board__number text-uppercase" v-if="indexRow === 0 || indexCol === 0">
              {{ String.fromCharCode(97 + indexRow) }} {{ indexCol + 1 }}
            </span>

            <!-- TEST BLOCKS -->
            <p v-if="wells[indexRow][indexCol].test">{{ wells[indexRow][indexCol].test }}</p>
            <!-- ACTIVE BLOCKS -->
            <template v-else>
              <draggable
                v-if="wellIsReady"
                :list="wells[indexRow][indexCol]"
                :options="{ group: { name: 'blocks', put: checkBlocks }, sort: false }"
                delay = 0,
                @add="addBlock"
                class="board__blocks">
                <li v-for="(block, blockIndex) in wells[indexRow][indexCol]" :key="blockIndex"
                  class="blocks__item" :data-block-type="block.block_type">
                  <div :class="['blocks__button text-uppercase', block.block_type]">
                    <span
                      @click="removeBlock(wells[indexRow][indexCol], blockIndex)"
                      class="blocks__remove">×
                    </span>
                    {{ block.name }}
                  </div>
                </li>
              </draggable>
            </template>
          </div>
        </div>
      </div>
      <div>
        <div class="info__box mt-5 d-flex justify-content-center">
          <b-btn
          v-if="!savedExperiment && !anotherExperiment"
            variant="primary"
            size="sm"
            class="my-2"
            @click="checkWells()">
            {{$t("save")}}
          </b-btn>
          <b-btn
          v-if="!savedExperiment && anotherExperiment"
            variant="primary"
            size="sm"
            class="my-2"
            @click="checkWells()">
            {{$t("saveNew")}}
          </b-btn>
          <b-btn
            v-if="savedExperiment"
            variant="primary"
            size="sm"
            class="my-2"
            @click="getProtocol(savedExperiment.id)">
            {{ $t("generateProtocol") }}
          </b-btn>
          <router-link
            v-if="savedExperiment"
            :to="{ path: `/report_my_results`}"
            tag="button"
            class="btn btn-primary btn-sm my-2 ml-4" v-scroll-to="'#scrollToOnLoad'">
            Report result
          </router-link>
          <b-btn variant="primary" size="sm" class="my-2 ml-4" @click="print">
            {{$t("results.print")}}
          </b-btn>
        </div>
      </div>

    <experiment-modal
      v-if="modalVisible"
      @hidden="modalVisible = false"
      @submit="createExperiment"
      :errors="errors"
      :duplicates="duplicates"
      :title="title">
    </experiment-modal>
  </section>
</template>

<script>
import draggable from 'vuedraggable';
import game from '@/api/v1/game';
import ExperimentModal from '@/views/Game/BuldingBlocks/Board/CreateExperimentModal';
import generateEmptyBoard from '@/utils/generateEmptyBoard';
import downloadFile from '@/utils/downloadFile';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

function formWell(well, x, y) {
  const formedWell = {
    x_position: x,
    y_position: y,
    monomers: [],
  };
  well.forEach((item) => {
    switch (item.block_type) {
      case 'monomer':
        formedWell.monomers.push(item.id);
        break;
      case 'terminator':
        formedWell.terminator_id = item.id;
        break;
      case 'initiator':
        formedWell.initiator_id = item.id;
        break;
      default:
    }
  });

  return formedWell;
}

function isWellValid(well) {
  return 'terminator_id' in well && 'initiator_id' in well;
}

function generateWells(wells) {
  const validWells = [];
  const invalidWells = [];

  wells.forEach((row, y) => {
    row.forEach((well, x) => {
      if (well.length) {
        const formedWell = formWell(well, x, y);
        if (isWellValid(formedWell)) validWells.push(formedWell);
        else invalidWells.push(formedWell);
      }
    });
  });

  return { valid: validWells, invalid: invalidWells };
}

export default {
  props: ['blocks', 'isEditable'],
  components: {
    draggable,
    ExperimentModal,
  },
  data() {
    return {
      id: this.$route.params.experimentId,
      wells: [],
      borderColors: ['#e85900', '#bed6eb', '#083b66'],
      domain: process.env.SERVER_URL,
      showInfo: false,
      modalVisible: false,
      wellIsReady: false,
      duplicates: [],
      boardDimensions: {
        height: 5,
        width: 8,
      },
      errors: [],
      title: '',
      savedExperiment: null,
      anotherExperiment: null,
    };
  },
  methods: {
    addBlock() {
      this.$forceUpdate();
    },
    removeBlock(from, blockIndex) {
      from.splice(blockIndex, 1);
      this.$forceUpdate();
    },
    checkBlocks(to, from, el) {
      const destinationList = to.el.__vue__.list; //eslint-disable-line
      const typesInList = destinationList.map(item => item.block_type);
      const draggingBlockType = el.dataset.blockType;
      if (draggingBlockType === 'monomer' && destinationList.length) {
        const monomersCounter = typesInList.filter(item => item === 'monomer').length;
        return monomersCounter < 2;
      }
      return typesInList.indexOf(draggingBlockType) === -1;
    },
    createExperiment(title) {
      if (this.isEditable) {
        const wells = generateWells(this.wells);
        const experiment = {
          title,
          wells: wells.valid,
          height: this.boardDimensions.height,
          width: this.boardDimensions.width,
        };
        game.updateExperiments(experiment, this.id);
      } else {
        const wells = generateWells(this.wells);
        const experiment = {
          title,
          wells: wells.valid,
          height: this.boardDimensions.height,
          width: this.boardDimensions.width,
        };
        const succ = (response) => {
          this.savedExperiment = response.data.experiment;
          this.anotherExperiment = false;
        };
        game.createExperiments(experiment, succ);
      }
      this.modalVisible = false;
    },
    getExperiment() {
      const succ = (response) => {
        const experiment = response.data.experiment;
        // Generate empty board
        this.wells = generateEmptyBoard(experiment.height, experiment.width);

        // Fill in existing wells
        this.fillWells(experiment);
        this.wellIsReady = true;
        this.title = experiment.title;
      };
      game.getExperiment(this.id, succ);
    },
    checkWells() {
      const wells = generateWells(this.wells);
      // Incomplete wells:
      this.errors = wells.invalid;

      // Duplicated wells:
      const wellsUniqueCodes = wells.valid.map((item) => {
        let uniqueCode = `${item.terminator_id};${item.initiator_id};`;
        const monomersIds = [];
        item.monomers.forEach((monomer) => {
          monomersIds.push(`${monomer};`);
        });
        uniqueCode = uniqueCode.concat(monomersIds.sort());
        return uniqueCode;
      });
      const duplicatedWells = [];
      wellsUniqueCodes.forEach((code, index) => {
        if (wellsUniqueCodes.indexOf(code) !== index) {
          duplicatedWells.push(wells.valid[index]);
        }
      });
      this.duplicates = duplicatedWells;

      this.modalVisible = true;
    },
    fillWells(experiment) {
      experiment.wells.forEach((well) => {
        const blocks = [];
        blocks.push(
          {
            block_type: 'initiator',
            name: well.initiator,
            id: well.initiator_id,
          },
          {
            block_type: 'terminator',
            name: well.terminator,
            id: well.terminator_id,
          },
        );
        well.monomers.forEach((monomer) => {
          blocks.push({
            block_type: 'monomer',
            name: monomer.name,
            id: monomer.id,
          });
        });
        this.wells[well.y_position][well.x_position] = blocks;
      });
    },
    updateWells() {
      if (this.isEditable) {
        this.getExperiment();
      } else {
        this.title = '';
        this.wells = generateEmptyBoard(
          this.boardDimensions.height,
          this.boardDimensions.width,
        );
        this.wellIsReady = true;
      }
    },
    getProtocol(id) {
      const succ = (response) => {
        downloadFile(
          process.env.SERVER_URL + response.data.experiment.protocol,
          'protocol.pdf',
        );
      };
      game.getProtocol(id, succ);
    },
    print() {
      const filename = 'Experiment.pdf';
      html2canvas(document.querySelector('#pdf-game-board'), { scale: 1 }).then((canvas) => {
        const pdf = new jsPDF('p', 'mm', 'a4'); // eslint-disable-line
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 20, 190, 110);
        pdf.save(filename);
      });
    },
    generateEmptyBoard,
  },
  watch: {
    isEditable() {
      this.updateWells();
    },
    wells() {
      if (this.savedExperiment) {
        this.anotherExperiment = true;
        this.savedExperiment = false;
      }
    },
  },
  created() {
    this.updateWells();
  },
};
</script>

<style lang='scss' scoped>
@import "./src/styles/helpers/_variables.scss";
@import "./src/styles/helpers/_include-media.scss";
@import "./src/styles/helpers/game_style.scss";
</style>



// WEBPACK FOOTER //
// src/views/Game/BuldingBlocks/Board/Index.vue